import { Injectable, inject } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Service responsible for initializing Firebase Auth
 * This service is used by the APP_INITIALIZER to ensure auth state is loaded
 * before the app starts routing
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInitializerService {
  private auth: Auth = inject(Auth);
  private authService: AuthService = inject(AuthService);
  
  // Track if auth is ready (initialized)
  private authReadySubject = new BehaviorSubject<boolean>(false);
  readonly authReady$ = this.authReadySubject.asObservable();
  
  constructor() {
    console.log('AuthInitializerService: Initializing');
  }
  
  /**
   * Initialize Firebase Auth
   * This is called by the APP_INITIALIZER
   * @returns A promise that resolves when Firebase Auth is ready
   */
  initializeAuth(): Promise<boolean> {
    console.log('AuthInitializerService: Initializing Firebase Auth');
    
    return new Promise<boolean>((resolve) => {
      // If auth is already ready, resolve immediately
      if (this.authReadySubject.value) {
        console.log('AuthInitializerService: Auth already initialized');
        resolve(true);
        return;
      }
      
      // Use Firebase's recommended pattern for auth state restoration
      const unsubscribe = onAuthStateChanged(this.auth, async (user: User | null) => {
        console.log('AuthInitializerService: Auth state changed:', user ? `User: ${user.email}` : 'No user');
        
        // Clean up the listener
        unsubscribe();
        
        if (user) {
          try {
            // Update user state
            await this.authService.refreshUserState();
            console.log('AuthInitializerService: User state refreshed successfully');
          } catch (error) {
            console.error('AuthInitializerService: Error refreshing user state:', error);
          }
        }
        
        // Mark auth as ready
        this.authReadySubject.next(true);
        resolve(true);
      }, (error) => {
        console.error('AuthInitializerService: Error in auth state change listener:', error);
        // Mark auth as ready even on error to avoid blocking the app
        this.authReadySubject.next(true);
        resolve(false);
      });
      
      // Set a timeout to avoid hanging indefinitely
      setTimeout(() => {
        if (!this.authReadySubject.value) {
          console.log('AuthInitializerService: Auth initialization timeout reached');
          this.authReadySubject.next(true);
          resolve(false);
        }
      }, 2000);
    });
  }
  
  /**
   * Check if auth is ready
   * @returns True if auth is ready, false otherwise
   */
  isAuthReady(): boolean {
    return this.authReadySubject.value;
  }
} 